// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import {
  spacer8,
  spacer16,
  spacer24,
  spacer80,
  screenSmMin,
  screenMdMin,
  screenMdMax,
  screenLgMin,
  spacer32,
  screenXsMax,
  Type,
} from '@spotify-internal/encore-web';
import { ModuleType } from '../types';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  Box,
  ImageAspectBox1x1,
  fadeInAnimation,
  withAnimationContext,
  CTAButton,
  DELAYS,
} from '../../ui';
import { formatText } from '../../utils';
import { Colors } from '../HeroNarrativeCarouselModule/Options/colorsType';

const HeroBody = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  height: auto;

  &.tallest {
    height: 100%;
  }

  @media (max-width: ${screenXsMax}) {
    flex-direction: column;
    padding-bottom: ${spacer32};
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  width: 30%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;

  @media (max-width: ${screenMdMax}) {
    width: 40%;
  }

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const InnerText = styled.div`
  padding-left: ${spacer80};
  width: 50%;
  height: auto;
  max-width: 600px;
  align-self: center;
  margin-top: auto;
  margin-bottom: auto;

  @media (min-width: ${screenLgMin}) {
    max-width: 750px;
    margin-left: 7%;
  }

  @media (max-width: ${screenMdMax}) {
    width: 55%;
  }

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    max-width: none;
    padding-top: ${spacer16};
    padding-left: 0px;
    margin: 0px;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
  })`
    ${fadeInAnimation(DELAYS.FIRST)};
    padding-bottom: ${spacer16};
    max-width: 684px;

    @media (min-width: ${screenMdMin}) {
      text-align: left;
    }
  `,
);

const BodyText = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: ${spacer24};

    @media (min-width: ${screenMdMin}) {
      padding-top: ${spacer16};
    }

    @media (max-width: ${screenSmMin}) {
      padding-bottom: ${spacer8};
    }
  `,
);

const CTAContainer = withAnimationContext(
  styled.div`
    ${fadeInAnimation(DELAYS.THIRD)};
    display: flex;
    padding-top: ${spacer16};
    padding-bottom: ${spacer24};

    @media (max-width: ${screenSmMin}) {
      width: 100%;
      justify-content: center;
    }
  `,
);

type HeroBodyModuleType = ModuleType & {
  headline?: string;
  bodyText?: string;
  image?: ContentfulAsset;
  ctaLink?: string;
  ctaText?: string;
  backgroundColor?: string;
  textColor?: string;
};

const TRACK_CAT = 'module-heroBody';

export const HeroBodyModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeroBodyModuleType
>(
  (
    { headline, bodyText, image, ctaLink, ctaText, backgroundColor, textColor },
    ref,
  ) => {
    return (
      <HeroBody
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        {image && (
          <ImageContainer>
            <ContentfulImage asset={image} />
          </ImageContainer>
        )}

        <InnerText>
          {headline && (
            <Headline
              data-testid="hero-body-headline"
              dangerouslySetInnerHTML={{ __html: formatText(headline) }}
            />
          )}
          {bodyText && (
            <BodyText
              dangerouslySetInnerHTML={{ __html: formatText(bodyText) }}
            />
          )}
          {ctaText && ctaLink && (
            <CTAContainer>
              <CTAButton
                ctaText={ctaText}
                ctaLink={ctaLink}
                title={ctaText}
                trackingCategory={TRACK_CAT}
                textColor={backgroundColor}
                backgroundColor={textColor}
              />
            </CTAContainer>
          )}
        </InnerText>
      </HeroBody>
    );
  },
);
